import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Asset, getPrimaryOwnerId } from '../types/Asset';
import { getAssets } from '../services/assetService';
import { Spinner, Grid } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { getContacts, Contact } from '../../contacts';
import { useAssetSettings } from '../context/AssetSettingsContext';
import { assetTypePath, assetsListPath, detailsSearchText, getAssetTypeBySlug, getAssetTypeModule, listFieldsFor } from '../typeCatalog';
import { AssetTypeIcon, ButtonWithIcon, CreateIcon, SettingsIcon } from './AssetIcons';
import iconStyles from './AssetIcons.module.css';
import styles from './Assets.module.css';

type SortField = 'name' | 'identifier' | string;
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

const detailValue = (asset: Asset, key: string): string => {
  const value = asset.details[key];
  if (value === null || value === undefined || value === false || value === '') {
    return '';
  }
  if (value === true) {
    return 'true';
  }
  return String(value);
};

export const Assets: React.FC = () => {
  const { t } = useTranslation(['assets', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const { isModuleEnabled, enabledTypes, loading: settingsLoading } = useAssetSettings();
  const requestedModule = getAssetTypeBySlug(searchParams.get('type') || undefined);
  const module = requestedModule && enabledTypes.includes(requestedModule.type)
    ? requestedModule
    : enabledTypes[0]
      ? getAssetTypeModule(enabledTypes[0])
      : undefined;
  const [assets, setAssets] = useState<Asset[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'name', direction: 'asc' });
  const canCreateAsset = hasPermission(PermissionScopes.assetsCreate) && Boolean(module);
  const canViewSettings = hasPermission(PermissionScopes.assetsRead);
  const extraFields = module ? listFieldsFor(module.type) : [];

  useEffect(() => {
    if (settingsLoading) {
      return;
    }

    if (!module) {
      setAssets([]);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const [data, contactData] = await Promise.all([
          getAssets({ assetType: module.type, page: 1, pageSize: 200 }),
          getContacts(),
        ]);
        setAssets(data);
        setContacts(contactData);
        setError(null);
      } catch {
        setError(t('failedToLoadAssets', { ns: 'assets' }));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [module, settingsLoading, t]);

  const contactNameById = useMemo(() => Object.fromEntries(contacts.map(contact => [contact.id, contact.name])), [contacts]);

  const getPrimaryContactDisplay = useCallback((asset: Asset) => {
    const primaryId = getPrimaryOwnerId(asset);
    return primaryId ? contactNameById[primaryId] || t('contactFallback', { ns: 'assets', id: primaryId.slice(0, 8) }) : '-';
  }, [contactNameById, t]);

  const handleSort = (field: SortField) => {
    const direction: SortDirection = sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ field, direction });
  };

  const filteredAndSorted = useMemo(() => {
    let list = assets;

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      list = list.filter(asset =>
        asset.name.toLowerCase().includes(query)
        || (asset.identifier || '').toLowerCase().includes(query)
        || getPrimaryContactDisplay(asset).toLowerCase().includes(query)
        || detailsSearchText(asset.assetType, asset.details).includes(query)
      );
    }

    return [...list].sort((left, right) => {
      const leftValue = (sortConfig.field === 'identifier'
        ? left.identifier || ''
        : sortConfig.field === 'name'
          ? left.name
          : detailValue(left, sortConfig.field)).toLowerCase();
      const rightValue = (sortConfig.field === 'identifier'
        ? right.identifier || ''
        : sortConfig.field === 'name'
          ? right.name
          : detailValue(right, sortConfig.field)).toLowerCase();

      if (leftValue < rightValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (leftValue > rightValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [assets, getPrimaryContactDisplay, searchTerm, sortConfig]);

  if (settingsLoading || loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loadingAssets', { ns: 'assets' })} /></div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>{t('title', { ns: 'assets' })}</h2>
        </div>
        <div className={styles.headerActions}>
          {canViewSettings ? (
            <button className={`btn btn-secondary ${iconStyles.buttonWithIcon}`} type="button" onClick={() => navigate('/assets/settings')}>
              <ButtonWithIcon icon={<SettingsIcon />}>{t('settings', { ns: 'assets' })}</ButtonWithIcon>
            </button>
          ) : null}
          {canCreateAsset && module ? (
            <button className={`btn btn-primary ${iconStyles.buttonWithIcon}`} type="button" onClick={() => navigate(assetTypePath(module.type, '/add'))}>
              <ButtonWithIcon icon={<CreateIcon />}>{t('addAsset', { ns: 'assets' })}</ButtonWithIcon>
            </button>
          ) : null}
        </div>
      </div>

      {enabledTypes.length > 0 ? (
        <div className={styles.typeTabs}>
          {enabledTypes.map(type => {
            const option = getAssetTypeModule(type);
            const isActive = module?.type === type;
            return (
              <button
                key={type}
                type="button"
                className={isActive ? styles.typeTabActive : styles.typeTab}
                onClick={() => navigate(assetsListPath(type))}
              >
                <span className={iconStyles.typeTabLabel}>
                  <AssetTypeIcon type={type} />
                  {t(option.labelKey, { ns: 'assets' })}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <input
            className={`search-input ${styles.searchInput}`}
            type="text"
            placeholder={t('searchPlaceholder', { ns: 'assets' })}
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
          />
        </div>
      </div>

      {filteredAndSorted.length === 0 ? (
        <div className={styles.emptyState}>
          {isModuleEnabled ? t('empty', { ns: 'assets' }) : t('noEnabledTypesHint', { ns: 'assets' })}
        </div>
      ) : (
        <Grid
          columns={[
            {
              key: 'name',
              label: t('name', { ns: 'assets' }),
              sortable: true,
              className: styles.nameColumn,
              headerClassName: styles.nameColumn,
              render: (asset: Asset) => {
                const photo = asset.attachments.find(item => item.kind === 'image' && item.isPrimary)
                  || asset.attachments.find(item => item.kind === 'image');
                return (
                  <div className={styles.nameCell}>
                    {photo ? <img className={styles.listThumb} src={photo.url} alt="" /> : <span className={styles.listThumbPlaceholder} />}
                    <div className={styles.valueStack}>
                      <span className={styles.primaryText}>{asset.name}</span>
                      <span className={styles.secondaryText}>{asset.identifier || '-'}</span>
                    </div>
                  </div>
                );
              },
            },
            ...extraFields.map(field => ({
              key: field.key,
              label: t(field.labelKey, { ns: 'assets' }),
              sortable: true,
              className: styles.typeColumn,
              headerClassName: styles.typeColumn,
              render: (asset: Asset) => (
                <span className={`${styles.valueText} ${detailValue(asset, field.key) ? '' : styles.mutedValue}`}>
                  {detailValue(asset, field.key) || '-'}
                </span>
              ),
            })),
            {
              key: 'contact',
              label: t('owners', { ns: 'assets' }),
              sortable: false,
              className: styles.contactsColumn,
              headerClassName: styles.contactsColumn,
              render: (asset: Asset) => (
                <span className={`${styles.valueText} ${getPrimaryContactDisplay(asset) === '-' ? styles.mutedValue : ''}`}>
                  {getPrimaryContactDisplay(asset)}
                  {asset.owners.length > 1 ? ` +${asset.owners.length - 1}` : ''}
                </span>
              ),
            },
            {
              key: 'identifier',
              label: t(module?.identifierLabelKey || 'identifiers.identifier', { ns: 'assets' }),
              sortable: true,
              className: styles.identifierColumn,
              headerClassName: styles.identifierColumn,
              render: (asset: Asset) => (
                <span className={`${styles.valueText} ${asset.identifier ? '' : styles.mutedValue}`}>{asset.identifier || '-'}</span>
              ),
            },
          ]}
          rows={filteredAndSorted}
          rowKey={(asset: Asset) => asset.id}
          onRowClick={(asset: Asset) => navigate(assetTypePath(asset.assetType, `/${asset.id}`))}
          sortKey={sortConfig.field}
          sortDirection={sortConfig.direction}
          onSort={(key) => handleSort(key as SortField)}
        />
      )}
    </div>
  );
};

export default Assets;
